import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, friendlyError, logIssue } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { CollectionEditor, Field, Toast } from "@/components/admin/Editors";
import { MediaField } from "@/components/admin/Media";
import { Illu, ILLUSTRATION_OPTIONS } from "@/components/site/Illustrations";
import MediaManager from "@/pages/admin/MediaManager";
import {
  LayoutDashboard, FileText, Images, Sparkles, Inbox, Package, Gauge, LogOut, ImagePlus,
  Loader2, Save, Upload, RotateCcw, Smartphone, Tablet, Monitor, ExternalLink, Camera,
} from "lucide-react";

const NAV = [
  ["overview", "Übersicht", LayoutDashboard],
  ["texts", "Inhalte", FileText],
  ["media", "Medien", ImagePlus],
  ["works", "Arbeiten", Images],
  ["motifs", "Verfügbare Motive", Sparkles],
  ["faqs", "FAQ", FileText],
  ["inquiries", "Anfragen", Inbox],
  ["photography", "Fotografie", Camera],
  ["products", "Produkte & Bestellungen", Package],
  ["capacity", "Kapazität", Gauge],
];

const ILLU_SLOTS = [
  ["illu_works", "Ausgewählte Arbeiten"],
  ["illu_motifs", "Verfügbare Motive"],
  ["illu_custom", "Custom-Bereich"],
  ["illu_pos_1", "Position 01 – Ikonische Figur"],
  ["illu_pos_2", "Position 02 – Geometrischer Bruch"],
  ["illu_pos_3", "Position 03 – Erzählerische Welt"],
  ["illu_process_1", "Ablauf 01 – Anfrage"],
  ["illu_process_2", "Ablauf 02 – Rückmeldung"],
  ["illu_process_3", "Ablauf 03 – Entwicklung"],
  ["illu_process_4", "Ablauf 04 – Termin"],
  ["illu_archive", "Archivmarke"],
  ["illu_shop", "Artikelbereich"],
  ["illu_location", "Entdeckerprojekt / Standort"],
];

const MEDIA_SLOTS = [
  ["hero_image_media_id", "Hero-Bild"],
  ["artist_image_media_id", "Bild René & Studio"],
  ["social_preview_media_id", "Social-Preview-Bild"],
];

const TEXT_GROUPS = [
  ["Hero", ["hero_kicker", "hero_h1", "hero_sub", "hero_cta_primary", "hero_cta_secondary"]],
  ["Arbeiten & Position", ["works_h2", "works_intro", "positioning_h2", "positioning_body"]],
  ["Motive & Custom", ["motifs_h2", "motifs_intro", "custom_h2", "custom_body"]],
  ["Ablauf & René", ["process_h2", "process_intro", "artist_h2", "artist_body", "artist_region", "artist_note"]],
  ["Entdeckerprojekt", ["discover_h3", "discover_body", "discover_cta"]],
  ["Anfrage", ["inquiry_h2", "inquiry_help", "inquiry_consent", "inquiry_marketing", "inquiry_button", "inquiry_success_h", "inquiry_success_body"]],
  ["FAQ & Artikel", ["faq_h2", "shop_h2", "shop_intro", "shop_notice"]],
  ["Kontakt", ["instagram_url", "instagram_handle", "contact_email"]],
  ["Rechtstexte", ["legal_warning", "impressum_body", "datenschutz_body", "versand_body"]],
];

const LONG = ["hero_sub", "positioning_body", "custom_body", "artist_body", "inquiry_consent", "inquiry_marketing", "inquiry_success_body", "shop_intro", "shop_notice", "impressum_body", "datenschutz_body", "versand_body", "works_intro", "motifs_intro", "process_intro", "discover_body", "legal_warning", "artist_note"];

function Overview({ onGo }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get("/admin/overview")
      .then((r) => setData(r.data))
      .catch((e) => {
        logIssue("GET /admin/overview", e);
        setError(friendlyError(e));
      });
  }, []);
  if (error) return <p className="border border-destructive p-4 text-sm text-destructive" data-testid="overview-error">{error}</p>;
  if (!data) return <Loader2 className="animate-spin text-charcoal" />;
  const cards = [
    ["Neue Tattooanfragen", data.new_inquiries, "inquiries"],
    ["Offene Bestellanfragen", data.open_orders, "products"],
    ["Verfügbare Motive", data.motifs_available, "motifs"],
    ["Projektinfo-Zustimmungen", data.marketing_consents, "inquiries"],
  ];
  return (
    <div data-testid="admin-overview">
      <h2 className="ap-h3 text-ink">Übersicht</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(([label, value, target], i) => (
          <button
            key={label}
            type="button"
            onClick={() => onGo(target)}
            data-testid={`overview-card-${i}`}
            className="border border-paper-line bg-paper p-5 text-left transition-colors hover:border-signal"
          >
            <p className="font-display text-4xl font-black text-ink">{value}</p>
            <p className="ap-stamp mt-2 leading-snug">{label}</p>
          </button>
        ))}
      </div>
      <div className="mt-8 border border-paper-line bg-paper p-5">
        <p className="ap-stamp">Aktuelle Kapazität</p>
        <p className="mt-2 font-display text-lg font-bold uppercase text-ink">{data.capacity?.status}</p>
        <p className="ap-body mt-1 text-charcoal">{data.capacity?.note}</p>
      </div>
      <a href="/" target="_blank" rel="noreferrer" className="ap-btn-ghost mt-8" data-testid="overview-open-site">
        <ExternalLink size={15} /> Website ansehen
      </a>
    </div>
  );
}

function TextsEditor() {
  const [texts, setTexts] = useState(null);
  const [msg, setMsg] = useState("");
  const [invites, setInvites] = useState([]);
  const [inviteName, setInviteName] = useState("");
  const [newInviteUrl, setNewInviteUrl] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState("desktop");
  const [openGroup, setOpenGroup] = useState("Hero");

  useEffect(() => {
    api
      .get("/admin/texts?which=draft")
      .then((r) => setTexts(r.data.texts))
      .catch((e) => {
        logIssue("GET /admin/texts", e);
        setError(friendlyError(e));
      });
  }, []);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 2200);
  };

  const act = async (fn, label) => {
    setError("");
    try {
      await fn();
      flash(label);
    } catch (e) {
      logIssue("texts action", e);
      setError(friendlyError(e));
    }
  };

  if (!texts) {
    return error ? (
      <p className="border border-destructive p-4 text-sm text-destructive" data-testid="texts-error">{error}</p>
    ) : (
      <Loader2 className="animate-spin text-charcoal" />
    );
  }

  const width = { mobile: 390, tablet: 820, desktop: "100%" }[preview];

  return (
    <div data-testid="admin-texts">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="ap-h3 text-ink">Inhalte</h2>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => act(() => api.put("/admin/texts", { texts }), "Entwurf gespeichert")} className="ap-btn-solid" data-testid="texts-save-draft">
            <Save size={15} /> Entwurf speichern
          </button>
          <button
            type="button"
            onClick={() => act(async () => {
              await api.put("/admin/texts", { texts });
              await api.post("/admin/texts/publish");
            }, "Veröffentlicht")}
            className="ap-btn-ghost"
            data-testid="texts-publish"
          >
            <Upload size={15} /> Veröffentlichen
          </button>
          <button type="button" onClick={() => act(async () => {
            await api.post("/admin/texts/restore");
            const r = await api.get("/admin/texts?which=draft");
            setTexts(r.data.texts);
          }, "Version wiederhergestellt")} className="ap-btn-ghost" data-testid="texts-restore">
            <RotateCcw size={15} /> Letzte Version
          </button>
        </div>
      </div>

      {error ? <p className="mb-4 border border-destructive p-3 text-sm text-destructive">{error}</p> : null}

      <div className="mb-6 flex items-center gap-2 border border-paper-line bg-paper p-3">
        <span className="ap-stamp mr-2">Vorschau</span>
        {[["mobile", Smartphone], ["tablet", Tablet], ["desktop", Monitor]].map(([k, Icon]) => (
          <button
            key={k}
            type="button"
            onClick={() => setPreview(k)}
            aria-pressed={preview === k}
            data-testid={`preview-${k}`}
            className={`flex h-10 w-10 items-center justify-center border transition-colors ${
              preview === k ? "border-signal text-signal" : "border-paper-line text-charcoal"
            }`}
          >
            <Icon size={16} />
          </button>
        ))}
        <span className="ap-stamp ml-auto">{typeof width === "number" ? `${width}px` : "Volle Breite"}</span>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-3">
          <div className="border border-signal/40 bg-paper p-5" data-testid="texts-media-slots">
            <p className="font-display text-base font-bold uppercase tracking-wide text-ink">Bilder</p>
            <p className="ap-body mt-1 text-charcoal">
              Bilder kommen aus der Medienbibliothek – keine URL-Eingabe nötig.
            </p>
            <div className="mt-4 space-y-5">
              {MEDIA_SLOTS.map(([key, label]) => (
                <MediaField
                  key={key}
                  label={label}
                  value={texts[key] || ""}
                  onChange={(v) => setTexts((t) => ({ ...t, [key]: v }))}
                />
              ))}
            </div>
          </div>

          <div className="border border-paper-line bg-paper p-5" data-testid="texts-illu-slots">
            <p className="font-display text-base font-bold uppercase tracking-wide text-ink">Illustrationen</p>
            <p className="ap-body mt-1 text-charcoal">
              Jedem Bereich lässt sich eine der ArtPart-Zeichnungen zuweisen.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {ILLU_SLOTS.map(([key, label]) => (
                <div key={key}>
                  <Field
                    label={label}
                    value={texts[key] || ""}
                    options={ILLUSTRATION_OPTIONS}
                    onChange={(v) => setTexts((t) => ({ ...t, [key]: v }))}
                  />
                  <div className="ap-illu mt-2 h-14 w-14" aria-hidden="true">
                    <Illu name={texts[key]} className="h-full w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {TEXT_GROUPS.map(([group, keys]) => (
            <div key={group} className="border border-paper-line bg-paper">
              <button
                type="button"
                onClick={() => setOpenGroup((g) => (g === group ? "" : group))}
                className="flex w-full items-center justify-between px-5 py-4 text-left font-display text-base font-bold uppercase tracking-wide text-ink"
                data-testid={`text-group-${group}`}
              >
                {group}
                <span className="ap-stamp">{openGroup === group ? "−" : "+"}</span>
              </button>
              {openGroup === group ? (
                <div className="space-y-4 border-t border-paper-line p-5">
                  {keys.map((k) => (
                    <Field
                      key={k}
                      label={k}
                      rows={LONG.includes(k) ? (k.endsWith("_body") ? 10 : 3) : undefined}
                      value={texts[k] ?? ""}
                      onChange={(v) => setTexts((t) => ({ ...t, [k]: v }))}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div className="border border-paper-line bg-paper-subtle p-4">
          <p className="ap-stamp mb-3">Live-Vorschau (veröffentlichte Version)</p>
          <div className="mx-auto overflow-hidden border border-paper-line bg-paper" style={{ width, maxWidth: "100%" }}>
            <iframe title="Vorschau" src="/" className="h-[600px] w-full" data-testid="preview-frame" />
          </div>
        </div>
      </div>
      <Toast msg={msg} />
    </div>
  );
}

function Inquiries() {
  const [items, setItems] = useState(null);
  const [msg, setMsg] = useState("");
  const [invites, setInvites] = useState([]);
  const [inviteName, setInviteName] = useState("");
  const [newInviteUrl, setNewInviteUrl] = useState("");

  const load = () =>
    api
      .get("/admin/inquiries")
      .then((r) => setItems(r.data))
      .catch((e) => {
        logIssue("GET /admin/inquiries", e);
        setItems([]);
        setMsg("Anfragen konnten nicht geladen werden.");
      });
  useEffect(() => {
    load();
  }, []);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, body);
      setMsg("Gespeichert");
      setTimeout(() => setMsg(""), 1800);
      load();
    } catch (e) {
      logIssue("PATCH /admin/inquiries", e);
      setMsg(friendlyError(e));
      setTimeout(() => setMsg(""), 3000);
    }
  };

  if (!items) return <Loader2 className="animate-spin text-charcoal" />;

  return (
    <div data-testid="admin-inquiries">
      <h2 className="ap-h3 text-ink">Anfragen ({items.length})</h2>
      {items.length === 0 ? <p className="ap-body mt-6 text-charcoal">Noch keine Anfragen eingegangen.</p> : null}
      <div className="mt-6 space-y-4">
        {items.map((q, i) => (
          <article key={q.id} data-testid={`inquiry-item-${i}`} className="border border-paper-line bg-paper p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h3 className="font-display text-lg font-bold uppercase tracking-wide text-ink">{q.name}</h3>
              <span className="ap-stamp">{new Date(q.created_at).toLocaleString("de-AT")}</span>
            </div>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              {[
                ["E-Mail", q.email], ["Telefon", q.phone], ["Kontaktweg", q.contact_channel],
                ["Projektart", q.project_type], ["Motiv", q.motif_title], ["Körperstelle", q.placement],
                ["Größe", q.size], ["Quelle", q.source], ["Projektinfos", q.marketing_consent ? "ja" : "nein"],
                ["E-Mail-Versand", q.email_status || "—"],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="ap-stamp shrink-0">{k}</dt>
                  <dd className="text-charcoal">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="ap-body mt-3 whitespace-pre-line border-l-2 border-signal pl-3 text-charcoal">{q.description}</p>
            {q.files?.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {q.files.map((f) => (
                  <span key={f} className="ap-stamp border border-paper-line px-2 py-1">Referenzbild vorhanden</span>
                ))}
              </div>
            ) : null}
            <div className="mt-4 grid gap-3 sm:grid-cols-[160px_1fr_auto] sm:items-end">
              <Field
                label="Status"
                value={q.status}
                options={[["neu", "neu"], ["in Kontakt", "in Kontakt"], ["angenommen", "angenommen"], ["abgelehnt", "abgelehnt"], ["erledigt", "erledigt"]]}
                onChange={(v) => patch(q.id, { status: v })}
              />
              <Field label="Interne Notiz" value={q.internal_note || ""} onChange={(v) => setItems((l) => l.map((x) => (x.id === q.id ? { ...x, internal_note: v } : x)))} />
              <button type="button" onClick={() => patch(q.id, { internal_note: q.internal_note || "" })} className="ap-btn-ghost" data-testid={`inquiry-save-${i}`}>
                <Save size={15} /> Notiz
              </button>
            </div>
          </article>
        ))}
      </div>
      <Toast msg={msg} />
    </div>
  );
}

function PhotographyInquiries() {
  const [items, setItems] = useState(null);
  const [msg, setMsg] = useState("");
  const [invites, setInvites] = useState([]);
  const [inviteName, setInviteName] = useState("");
  const [newInviteUrl, setNewInviteUrl] = useState("");

  const load = () =>
    api
      .get("/admin/photography-inquiries")
      .then((r) => setItems(r.data))
      .catch((e) => {
        logIssue("GET /admin/photography-inquiries", e);
        setItems([]);
        setMsg("Anfragen konnten nicht geladen werden.");
      });
  const loadInvites = () => api.get("/admin/photography-invites").then((r) => setInvites(r.data)).catch(() => setInvites([]));

  useEffect(() => {
    load();
      loadInvites();
  }, []);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/photography-inquiries/${id}`, body);
      setMsg("Gespeichert");
      setTimeout(() => setMsg(""), 1800);
      load();
    } catch (e) {
      logIssue("PATCH /admin/photography-inquiries", e);
      setMsg(friendlyError(e));
      setTimeout(() => setMsg(""), 3000);
    }
  };

  const createInvite = async () => {
    try {
      const r = await api.post("/admin/photography-invites", { name: inviteName });
      setNewInviteUrl(r.data.url);
      setInviteName("");
      loadInvites();
    } catch (e) {
      setMsg(friendlyError(e));
    }
  };

  if (!items) return <Loader2 className="animate-spin text-charcoal" />;

  return (
    <div data-testid="admin-photography-inquiries">
      <h2 className="ap-h3 text-ink">Fotografie-Anfragen ({items.length})</h2>
      <section className="mt-6 border border-paper-line bg-paper p-5">
        <h3 className="font-display text-lg font-bold uppercase text-ink">Persönliche Einladung erstellen</h3>
        <p className="ap-body mt-2 text-charcoal">Für deine Kund:innen: Das Shooting kostet sie nichts, ARTPART übernimmt die Kosten.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input className="ap-field flex-1" placeholder="Name des Kunden" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
          <button type="button" className="ap-btn-solid" disabled={!inviteName.trim()} onClick={createInvite}>Einladungslink erstellen</button>
        </div>
        {newInviteUrl ? <div className="mt-4 border-l-2 border-signal pl-4"><p className="ap-stamp">Persönlicher Link</p><a className="mt-2 block break-all text-sm underline" href={newInviteUrl} target="_blank" rel="noreferrer">{newInviteUrl}</a><button type="button" className="ap-btn-ghost mt-3" onClick={() => navigator.clipboard.writeText(newInviteUrl)}>Link kopieren</button></div> : null}
        <p className="ap-stamp mt-4">Einladungen: {invites.length} · verwendet: {invites.filter((x) => x.used).length}</p>
        {invites.length ? <div className="mt-4 space-y-2">
          {invites.map((x) => <div key={x.id} className="flex flex-col gap-2 border-t border-paper-line pt-3 sm:flex-row sm:items-center sm:justify-between">
            <div><strong>{x.name || "Ohne Namen"}</strong><span className="ml-2 ap-stamp">{x.used ? "verwendet" : "offen"}</span></div>
            <button type="button" className="ap-btn-ghost" onClick={() => navigator.clipboard.writeText(`https://www.artpart.at/fotografie?einladung=${x.code}`)}>Link kopieren</button>
          </div>)}
        </div> : null}
      </section>
      {items.length === 0 ? <p className="ap-body mt-6 text-charcoal">Noch keine Fotografie-Anfragen eingegangen.</p> : null}
      <div className="mt-6 space-y-4">
        {items.map((q, i) => (
          <article key={q.id} data-testid={`photography-item-${i}`} className="border border-paper-line bg-paper p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h3 className="font-display text-lg font-bold uppercase tracking-wide text-ink">{q.name}</h3>
              <span className="ap-stamp">{new Date(q.created_at).toLocaleString("de-AT")}</span>
            </div>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              {[
                ["E-Mail", q.email], ["Telefon", q.phone],
                ["Körperstelle", q.placement], ["Wunschtermin", q.preferred_date],
                ["Anfrageart", q.inquiry_type === "artpart_invitation" ? "ARTPART-Einladung" : "Regulär"],
                ["Bildfreigabe", q.image_consent ? "ja" : "nein"], ["E-Mail-Versand", q.email_status || "—"],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <dt className="ap-stamp shrink-0">{k}</dt>
                  <dd className="text-charcoal">{v}</dd>
                </div>
              ))}
            </dl>
              <p className="ap-body mt-3 whitespace-pre-line border-l-2 border-signal pl-3 text-charcoal"><strong>Tattoo:</strong> {q.tattoo}</p>
              {q.message ? <p className="ap-body mt-3 whitespace-pre-line text-charcoal"><strong>Nachricht:</strong> {q.message}</p> : null}
            <div className="mt-4 grid gap-3 sm:grid-cols-[160px_1fr_auto] sm:items-end">
              <Field
                label="Status"
                value={q.status}
                  options={[["neu", "neu"], ["kontaktiert", "kontaktiert"], ["Termin vereinbart", "Termin vereinbart"], ["Shooting abgeschlossen", "Shooting abgeschlossen"], ["abgesagt", "abgesagt"]]}
                onChange={(v) => patch(q.id, { status: v })}
              />
              <Field label="Interne Notiz" value={q.internal_note || ""} onChange={(v) => setItems((l) => l.map((x) => (x.id === q.id ? { ...x, internal_note: v } : x)))} />
              <button type="button" onClick={() => patch(q.id, { internal_note: q.internal_note || "" })} className="ap-btn-ghost" data-testid={`photography-save-${i}`}>
                <Save size={15} /> Notiz
              </button>
            </div>
          </article>
        ))}
      </div>
      <Toast msg={msg} />
    </div>
  );
}

function Orders() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api
      .get("/admin/order-inquiries")
      .then((r) => setItems(r.data))
      .catch((e) => {
        logIssue("GET /admin/order-inquiries", e);
        setItems([]);
      });
  }, []);
  if (!items) return null;
  return (
    <div className="mt-14" data-testid="admin-orders">
      <h2 className="ap-h3 text-ink">Bestellanfragen ({items.length})</h2>
      {items.length === 0 ? <p className="ap-body mt-4 text-charcoal">Noch keine Bestellanfragen.</p> : null}
      <div className="mt-4 space-y-3">
        {items.map((o, i) => (
          <div key={o.id} data-testid={`order-item-${i}`} className="border border-paper-line bg-paper p-4 text-sm">
            <p className="font-display text-base font-bold uppercase text-ink">
              {o.product_title} · {o.quantity}× {o.variant}
            </p>
            <p className="mt-1 text-charcoal">
              {o.name} · {o.email} · {o.street}, {o.zip} {o.city}, {o.country}
            </p>
            {o.message ? <p className="mt-1 text-charcoal">{o.message}</p> : null}
            <p className="ap-stamp mt-2">Status: {o.status} · {new Date(o.created_at).toLocaleString("de-AT")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Capacity() {
  const [cap, setCap] = useState(null);
  const [msg, setMsg] = useState("");
  const [invites, setInvites] = useState([]);
  const [inviteName, setInviteName] = useState("");
  const [newInviteUrl, setNewInviteUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get("/admin/overview")
      .then((r) => setCap(r.data.capacity))
      .catch((e) => {
        logIssue("GET capacity", e);
        setError(friendlyError(e));
      });
  }, []);
  if (error) return <p className="border border-destructive p-4 text-sm text-destructive">{error}</p>;
  if (!cap) return <Loader2 className="animate-spin text-charcoal" />;
  return (
    <div data-testid="admin-capacity">
      <h2 className="ap-h3 text-ink">Kapazität</h2>
      <div className="mt-6 max-w-xl space-y-4 border border-paper-line bg-paper p-5">
        <Field
          label="Status"
          value={cap.status}
          options={[["open", "Anfragen geöffnet"], ["limited", "Nur wenige Projektplätze"], ["closed", "Keine neuen Projekte"], ["delayed", "Verlängerte Antwortzeit"]]}
          onChange={(v) => setCap({ ...cap, status: v })}
        />
        <Field label="Zusatztext" rows={3} value={cap.note} onChange={(v) => setCap({ ...cap, note: v })} />
        <label className="flex items-center gap-3 ap-body text-charcoal">
          <input type="checkbox" checked={cap.visible} onChange={(e) => setCap({ ...cap, visible: e.target.checked })} className="h-5 w-5 accent-signal" data-testid="capacity-visible" />
          Hinweis auf der Website anzeigen
        </label>
        <button
          type="button"
          onClick={async () => {
            try {
              await api.put("/admin/capacity", cap);
              setMsg("Gespeichert");
            } catch (e) {
              logIssue("PUT capacity", e);
              setMsg(friendlyError(e));
            }
            setTimeout(() => setMsg(""), 2500);
          }}
          className="ap-btn-solid"
          data-testid="capacity-save"
        >
          <Save size={15} /> Speichern
        </button>
      </div>
      <Toast msg={msg} />
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [user, setUser] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("artpart_token");
        nav("/admin/login", { replace: true });
      });
  }, [nav]);

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <Loader2 className="animate-spin text-charcoal" />
      </div>
    );
  }

  return (
    <>
      <Seo title="ArtPart Backend" description="Geschützter Bereich." path="/admin" noindex />
      <div className="min-h-screen bg-paper-surface">
        <header className="border-b border-paper-line bg-ink">
          <div className="ap-container flex h-[64px] items-center justify-between">
            <p className="font-display text-xl font-black uppercase tracking-[0.14em] text-paper">
              Art<span className="text-signal">Part</span> <span className="ap-stamp ml-2">Backend</span>
            </p>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem("artpart_token");
                nav("/admin/login", { replace: true });
              }}
              data-testid="admin-logout"
              className="ap-stamp flex items-center gap-2 transition-colors hover:text-signal"
            >
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </header>

        <div className="ap-container grid gap-8 py-8 lg:grid-cols-[220px_1fr]">
          <nav className="flex flex-wrap gap-2 lg:flex-col lg:items-stretch" aria-label="Backend-Navigation">
            {NAV.map(([k, label, Icon]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                aria-current={tab === k}
                data-testid={`admin-nav-${k}`}
                className={`flex min-h-[44px] items-center gap-2 border px-4 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${
                  tab === k ? "border-ink bg-ink text-paper" : "border-paper-line bg-paper text-charcoal hover:border-ink"
                }`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </nav>

          <main className="min-w-0">
            {tab === "overview" && <Overview onGo={setTab} />}
            {tab === "texts" && <TextsEditor />}
            {tab === "media" && <MediaManager />}
            {tab === "works" && (
              <CollectionEditor
                endpoint="works"
                testid="works-editor"
                title="Arbeiten"
                blank={{ title: "Neue Arbeit", code: "", category: "", placement: "", description: "", image: "", image_media_id: "", order: 99, featured: true, active: true }}
                fields={[
                  { key: "title", label: "Titel" },
                  { key: "code", label: "Projektcode" },
                  { key: "category", label: "Kategorie" },
                  { key: "placement", label: "Körperstelle" },
                  { key: "image_media_id", label: "Bild (aus Medienbibliothek)", type: "media", wide: true },
                  { key: "description", label: "Kurzbeschreibung", rows: 2, wide: true },
                  { key: "order", label: "Sortierung", type: "number" },
                  { key: "featured", label: "Auf Hauptseite (Ausgewählte Arbeiten)", type: "checkbox" },
                  { key: "active", label: "Aktiv", type: "checkbox" },
                ]}
              />
            )}
            {tab === "motifs" && (
              <CollectionEditor
                endpoint="motifs"
                testid="motifs-editor"
                title="Verfügbare Motive"
                blank={{ title: "Neues Motiv", code: "", category: "dystopisch", description: "", placement: "", size: "", status: "available", requestable: true, image: "", image_media_id: "", order: 99, visible: true }}
                fields={[
                  { key: "title", label: "Titel" },
                  { key: "code", label: "Projektnummer" },
                  { key: "category", label: "Kategorie", options: [["dystopisch", "Dystopisch"], ["wannado", "Anderes Wanna-do"]] },
                  { key: "status", label: "Status", options: [["available", "Verfügbar"], ["reserved", "Reserviert"], ["taken", "Vergeben"]] },
                  { key: "placement", label: "Körperstelle" },
                  { key: "size", label: "Größe" },
                  { key: "image_media_id", label: "Bild (aus Medienbibliothek)", type: "media", wide: true },
                  { key: "description", label: "Beschreibung", rows: 2, wide: true },
                  { key: "order", label: "Sortierung", type: "number" },
                  { key: "requestable", label: "Anfrage erlaubt", type: "checkbox" },
                  { key: "visible", label: "Sichtbar", type: "checkbox" },
                ]}
              />
            )}
            {tab === "faqs" && (
              <CollectionEditor
                endpoint="faqs"
                testid="faqs-editor"
                title="FAQ"
                blank={{ question: "Neue Frage", answer: "", order: 99, visible: true }}
                fields={[
                  { key: "question", label: "Frage", wide: true },
                  { key: "answer", label: "Antwort", rows: 4, wide: true },
                  { key: "order", label: "Sortierung", type: "number" },
                  { key: "visible", label: "Sichtbar", type: "checkbox" },
                ]}
              />
            )}
            {tab === "inquiries" && <Inquiries />}
            {tab === "photography" && <PhotographyInquiries />}
            {tab === "products" && (
              <>
                <CollectionEditor
                  endpoint="products"
                  testid="products-editor"
                  title="Produkte"
                  blank={{ title: "Neuer Artikel", description: "", price: "", variants: "", availability: "verfügbar", shipping_note: "", image: "", image_media_id: "", order: 99, visible: true }}
                  fields={[
                    { key: "title", label: "Titel" },
                    { key: "price", label: "Preis" },
                    { key: "variants", label: "Varianten (Komma getrennt)" },
                    { key: "availability", label: "Verfügbarkeit" },
                    { key: "image_media_id", label: "Bild (aus Medienbibliothek)", type: "media", wide: true },
                    { key: "description", label: "Beschreibung", rows: 2, wide: true },
                    { key: "shipping_note", label: "Versandhinweis", rows: 2, wide: true },
                    { key: "order", label: "Sortierung", type: "number" },
                    { key: "visible", label: "Sichtbar", type: "checkbox" },
                  ]}
                />
                <Orders />
              </>
            )}
            {tab === "capacity" && <Capacity />}
          </main>
        </div>
      </div>
    </>
  );
}
